import { Color } from "@repo/app-types/constants/colors";
import type { TeammatePlaybookToolUsageAccuracyScore } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { Tool } from "@repo/app-types/entities/tool";
import { Size } from "@repo/ui/helpers/helpers";
import { skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import type { FC } from "react";
import { useMemo } from "react";
import { GridList, GridListItem, type Key } from "react-aria-components";
import { computePassRatePillColor } from "../teammatePerformance/gradingPassRatePill.js";
import {
  PercentagePill,
  renderPercentage,
} from "../teammatePerformance/percentagePill.js";
import { TrendChart } from "../teammatePerformance/trendChart.js";

export interface ToolUsageAccuracyOverviewCardProps {
  /** List of tools to show. */
  toolIds: readonly string[];
  /** Map of tool ID to tool definition. Used to display tool names. */
  tools: ReadonlyMap<string, Tool>;
  /** Map of tool ID to tool usage accuracy score. */
  toolScores: ReadonlyMap<string, TeammatePlaybookToolUsageAccuracyScore>;
  isLoading?: boolean;
  /** Called when a tool row is clicked. */
  onToolClick?: (toolId: string) => void;
}

interface ToolRow {
  toolId: string;
  toolName: string;
  usedCallRatio: number | undefined;
  accurateCallRatio: number | undefined;
  accurateCallRatioTrendData: (number | null)[] | undefined;
}

export const ToolUsageAccuracyOverviewCard: FC<
  ToolUsageAccuracyOverviewCardProps
> = (props) => {
  const { onToolClick } = props;

  const toolRows = useMemo(() => {
    const rows: ToolRow[] = [];

    for (const toolId of props.toolIds) {
      const tool = props.tools.get(toolId);
      const score = props.toolScores.get(toolId);

      const usedCallRatio = score?.score.avg_used_call_ratio;

      const inaccurateCallRatio = score?.score.avg_inaccurate_call_ratio;
      const inaccurateCallRatioTrend =
        score?.score_trend.avg_inaccurate_call_ratio_sparkline;

      // Convert inaccurate ratio to accurate ratio
      const accurateCallRatio =
        inaccurateCallRatio !== undefined ? 1 - inaccurateCallRatio : undefined;

      const trendData = inaccurateCallRatioTrend?.map((ratio) =>
        ratio === null ? null : 1 - ratio,
      );

      rows.push({
        toolId,
        toolName: tool?.name ?? "Unknown tool",
        usedCallRatio,
        accurateCallRatio,
        accurateCallRatioTrendData: trendData,
      });
    }

    // Sort by tool name alphabetically
    rows.sort((a, b) => a.toolName.localeCompare(b.toolName));

    return rows;
  }, [props.toolIds, props.tools, props.toolScores]);

  const handleAction = (key: Key): void => {
    if (onToolClick === undefined) {
      return;
    }

    const toolId = typeof key === "string" ? key : undefined;

    if (toolId !== undefined) {
      onToolClick(toolId);
    }
  };

  if (props.isLoading) {
    return (
      <div className="rounded-lg border border-border-light bg-white">
        <div className="space-y-3 p-4">
          <div className={skeletonStyle({ className: "h-8 w-full" })} />
          <div className={skeletonStyle({ className: "h-8 w-full" })} />
        </div>
      </div>
    );
  }

  if (toolRows.length === 0) {
    return (
      <div className="rounded-lg border border-border-light bg-white p-4">
        <div className="text-sm text-muted-foreground">
          No tool usage data yet.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border-light bg-white">
      <GridList
        aria-label="Tool usage accuracy"
        className="divide-y divide-border-light"
        items={toolRows}
        onAction={onToolClick !== undefined ? handleAction : undefined}
      >
        {(row) => (
          <GridListItem
            id={row.toolId}
            className="flex items-center gap-4 px-4 py-2 first:rounded-t-lg last:rounded-b-lg data-[react-aria-pressable]:cursor-pointer data-[react-aria-pressable]:hover:bg-blue-50"
            textValue={row.toolName}
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm">{row.toolName}</div>
            </div>

            <div className="flex items-center gap-3">
              <PercentagePill
                size={Size.SMALL}
                value={row.usedCallRatio}
                computeColor={() => Color.Gray}
                renderPercentage={(percentage) =>
                  `${renderPercentage(percentage)} usage`
                }
              />

              <PercentagePill
                size={Size.SMALL}
                value={row.accurateCallRatio}
                computeColor={computePassRatePillColor}
                renderPercentage={(percentage) =>
                  `${renderPercentage(percentage)} accuracy`
                }
              />

              {row.accurateCallRatioTrendData ? (
                <TrendChart
                  className="w-24 self-stretch"
                  minYRange={0.01}
                  showsXAxisLabels={false}
                  showsYAxisLabels={true}
                  lines={[
                    {
                      key: row.toolId,
                      data: row.accurateCallRatioTrendData,
                    },
                  ]}
                  renderValue={renderPercentage}
                />
              ) : null}
            </div>
          </GridListItem>
        )}
      </GridList>
    </div>
  );
};
